/**
 * Offers Management Page
 *
 * CRUD interface for managing offers in the incentive program framework.
 * Displays offers with filtering by type, geographic scope, and status.
 */

import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  getOffers,
  getOfferWithRestrictions,
  createOffer,
  updateOffer,
  deleteOffer,
} from "../services/programs.service";
import type {
  ExtendedOffer,
  CreateOfferDTO,
  UpdateOfferDTO,
} from "../types/program.types";
import OfferFormModal from "../components/offers/OfferFormModal";
import { useToast } from "../components/ui/Toast";
import { formatCurrency } from "../utils/formatters";

// Filter options
const OFFER_TYPE_FILTERS = [
  { value: "all", label: "All Types" },
  { value: "VOUCHER_CODE", label: "Voucher Code" },
  { value: "MEMBER_BENEFIT", label: "Member Benefit" },
  { value: "EVENT_REGISTRATION", label: "Event Registration" },
  { value: "SUBSCRIPTION", label: "Subscription" },
  { value: "REFERRAL", label: "Referral" },
  { value: "SURVEY", label: "Survey" },
  { value: "EVENT_DRAWING", label: "Event Drawing Entry" },
];

const STATUS_FILTERS = [
  { value: "all", label: "All Status" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const SCOPE_FILTERS = [
  { value: "all", label: "All Scopes" },
  { value: "NATIONAL", label: "National" },
  { value: "MULTI_STATE", label: "Multi-State" },
  { value: "SINGLE_STATE", label: "Single State" },
];

export default function Offers() {
  const { showToast } = useToast();
  const [offers, setOffers] = useState<ExtendedOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingOffer, setEditingOffer] = useState<ExtendedOffer | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [scopeFilter, setScopeFilter] = useState("all");

  useEffect(() => {
    loadOffers();
  }, []);

  const loadOffers = async () => {
    try {
      setLoading(true);
      const data = await getOffers();
      setOffers(data);
    } catch (error) {
      console.error("Error loading offers:", error);
      showToast("error", "Error loading offers");
    } finally {
      setLoading(false);
    }
  };

  // Filter offers based on search and filters
  const filteredOffers = useMemo(() => {
    return offers.filter((offer) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          offer.name.toLowerCase().includes(query) ||
          offer.offer_code.toLowerCase().includes(query) ||
          offer.description?.toLowerCase().includes(query);
        if (!matchesSearch) {
          return false;
        }
      }

      // Type filter
      if (typeFilter !== "all" && offer.offer_type !== typeFilter) {
        return false;
      }

      // Status filter
      if (statusFilter !== "all") {
        const isActive = offer.is_active && !offer.is_deleted;
        if (statusFilter === "active" && !isActive) {
          return false;
        }
        if (statusFilter === "inactive" && isActive) {
          return false;
        }
      }

      // Scope filter
      if (scopeFilter !== "all" && offer.geographic_scope !== scopeFilter) {
        return false;
      }

      return true;
    });
  }, [offers, searchQuery, typeFilter, statusFilter, scopeFilter]);

  const handleCreateOffer = async (input: CreateOfferDTO) => {
    try {
      await createOffer(input);
      await loadOffers();
      setShowCreateModal(false);
      showToast("success", "Offer created successfully");
    } catch (error) {
      console.error("Error creating offer:", error);
      showToast("error", "Error creating offer");
      throw error;
    }
  };

  const handleEditOffer = async (offerId: string) => {
    try {
      const offer = await getOfferWithRestrictions(offerId);
      if (offer) {
        setEditingOffer(offer);
      }
    } catch (error) {
      console.error("Error loading offer:", error);
      showToast("error", "Error loading offer details");
    }
  };

  const handleUpdateOffer = async (input: UpdateOfferDTO) => {
    if (!editingOffer) {
      return;
    }

    try {
      await updateOffer(editingOffer.id, input);
      await loadOffers();
      setEditingOffer(null);
      showToast("success", "Offer updated successfully");
    } catch (error) {
      console.error("Error updating offer:", error);
      showToast("error", "Error updating offer");
      throw error;
    }
  };

  const handleDeleteOffer = async (offerId: string) => {
    if (!confirm("Are you sure you want to delete this offer?")) {
      return;
    }

    try {
      await deleteOffer(offerId);
      await loadOffers();
      showToast("success", "Offer deleted successfully");
    } catch (error) {
      console.error("Error deleting offer:", error);
      showToast("error", "Error deleting offer");
    }
  };

  const getValueDisplay = (offer: ExtendedOffer) => {
    if (offer.value_type === "PERCENTAGE" && offer.value_amount) {
      return `${offer.value_amount}% off`;
    }
    if (offer.value_type === "FIXED" && offer.value_amount) {
      return formatCurrency(offer.value_amount);
    }
    if (offer.value_type === "FREE") {
      return "Free";
    }
    if (offer.value_type === "BOGO") {
      return "BOGO";
    }
    return offer.value_description || "-";
  };

  const getScopeDisplay = (offer: ExtendedOffer) => {
    if (offer.geographic_scope === "NATIONAL") {
      return "National";
    }
    if (offer.allowed_states && offer.allowed_states.length > 0) {
      if (offer.allowed_states.length <= 3) {
        return offer.allowed_states.join(", ");
      }
      return `${offer.allowed_states.slice(0, 3).join(", ")} +${offer.allowed_states.length - 3}`;
    }
    return offer.geographic_scope?.replace("_", " ") || "-";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading offers...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Offers</h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage incentive offers for events and campaigns
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/admin/ticket-rewards"
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-600 transition-colors"
            >
              Ticket Rewards
            </Link>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-600 transition-colors"
            >
              Create Offer
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 items-center">
          {/* Search */}
          <div className="flex-1 min-w-[200px] max-w-md">
            <input
              type="text"
              placeholder="Search offers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Type Filter */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            {OFFER_TYPE_FILTERS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            {STATUS_FILTERS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          {/* Scope Filter */}
          <select
            value={scopeFilter}
            onChange={(e) => setScopeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            {SCOPE_FILTERS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Results count */}
      <div className="mb-4 text-sm text-gray-500">
        Showing {filteredOffers.length} of {offers.length} offers
      </div>

      {/* Offers Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Offer
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Value
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Scope
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Validity
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredOffers.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-6 py-12 text-center text-gray-500"
                >
                  {offers.length === 0
                    ? "No offers found. Create your first offer to get started."
                    : "No offers match your filters."}
                </td>
              </tr>
            ) : (
              filteredOffers.map((offer) => (
                <tr key={offer.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {offer.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {offer.offer_code}
                    </div>
                    {offer.description && (
                      <div className="text-sm text-gray-500 mt-1 line-clamp-1">
                        {offer.description}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {offer.offer_type?.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getValueDisplay(offer)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {getScopeDisplay(offer)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {offer.valid_from || offer.valid_to ? (
                      <div>
                        {offer.valid_from && (
                          <div>
                            {new Date(offer.valid_from).toLocaleDateString()}
                          </div>
                        )}
                        {offer.valid_to && (
                          <div className="text-xs text-gray-400">
                            to {new Date(offer.valid_to).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">No dates set</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        offer.is_active && !offer.is_deleted
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {offer.is_active && !offer.is_deleted
                        ? "Active"
                        : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                    <button
                      onClick={() => handleEditOffer(offer.id)}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteOffer(offer.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Offer Modal */}
      <OfferFormModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleCreateOffer}
        title="Create New Offer"
      />

      {/* Edit Offer Modal */}
      <OfferFormModal
        isOpen={!!editingOffer}
        onClose={() => setEditingOffer(null)}
        onSave={handleUpdateOffer}
        offer={editingOffer}
        title="Edit Offer"
      />
    </div>
  );
}
